import { FC, useEffect, useMemo } from 'react';
import { Avatar, Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { PlatformForm as Form } from '@/shared/components/forms';
import { getSchemaFieldIcon } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/schemaFieldIcon';
import { useReportComponentStore } from '@/stores';
import { ReportComponent, ReportComponentType } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { useDatasetSchema } from '../01-Overview/hooks';
import GroupedByDropdown from './components/GroupedByDropdown';
import MetricDropdown from './components/MetricDropdown';

interface AxesProps {
    component: ReportComponent;
}

const Axes: FC<AxesProps> = ({ component }): JSX.Element => {
    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();

    const def = REPORT_COMPONENT_DEFINITIONS[component.type];
    const caps = def.capabilities;

    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);
    const replaceDraftConfiguration = useReportComponentStore((s) => s.replaceDraftConfiguration);

    const draft: Partial<ReportComponentConfig> = draftConfigurations[component?.id as string];
    const { schema, isLoading: schemaLoading } = useDatasetSchema(draft as ReportComponentConfig);

    const { token } = useAntDesignTheme();

    const isReady = Boolean(draft?.dataSet?.type && draft?.dataSet?.key);
    const isFunnelChart = component.type === ReportComponentType.FUNNEL_CHART;
    const funnelAllowsMultipleMetrics =
        isFunnelChart &&
        draft?.dataSet?.type === DataSourceType.PLATFORM_FINANCIAL &&
        draft?.dataSet?.key === 'incomeStatement';
    const allowsMultipleMetrics =
        funnelAllowsMultipleMetrics || component.type === ReportComponentType.MULTIPLE_STATISTICS;
    const schemaMetricKeys = useMemo(() => new Set(schema.metrics.map((metric) => metric.key)), [schema.metrics]);
    const schemaDimensionKeys = useMemo(
        () => new Set(schema.dimensions.map((dimension) => dimension.key)),
        [schema.dimensions],
    );

    useEffect(() => {
        if (schemaLoading || !draft?.query || !schema.metrics.length) {
            return;
        }

        const metrics = draft.query.metrics ?? [];
        const dimensions = draft.query.dimensions ?? [];
        const nextMetrics = metrics.filter((metric) => schemaMetricKeys.has(metric.field));
        const nextDimensions = dimensions.filter((dimension) => schemaDimensionKeys.has(dimension.field));

        if (nextMetrics.length === metrics.length && nextDimensions.length === dimensions.length) {
            return;
        }

        replaceDraftConfiguration(component.id, {
            ...draft,
            data: undefined,
            query: {
                ...draft.query,
                metrics: nextMetrics,
                dimensions: nextDimensions,
            },
        });
    }, [
        component.id,
        draft,
        replaceDraftConfiguration,
        schema.dimensions.length,
        schema.metrics.length,
        schemaDimensionKeys,
        schemaLoading,
        schemaMetricKeys,
    ]);

    const metricSlots = isFunnelChart
        ? [funnelAllowsMultipleMetrics ? 'Metrics' : 'Metric']
        : component.type === ReportComponentType.SCATTER_PLOT
          ? ['X Metric', 'Y Metric']
          : ['Metric'];
    const dimensionSlots =
        component.type === ReportComponentType.MAP
            ? ['Latitude', 'Longitude']
            : [
                    ReportComponentType.HEAT_MAP,
                    ReportComponentType.SANKEY_CHART,
                    ReportComponentType.CHORD_CHART,
                ].includes(component.type)
              ? ['Source / Row', 'Target / Column']
              : [ReportComponentType.SCATTER_PLOT, ReportComponentType.GAUGE_CHART].includes(component.type) ||
                  (isFunnelChart && funnelAllowsMultipleMetrics)
                ? []
                : ['Grouped By'];

    // if (!draft) {
    //     return <></>;
    // }

    return (
        <>
            {/* {caps.xAxis?.metric && ( */}
            <div style={{ padding: '0 1rem' }}>
                <Row gutter={[16, 0]}>
                    {metricSlots.map((label, slot) => {
                        const selectedMetric = draft?.query?.metrics?.[slot]?.field;
                        const selectedMetricField = schema.metrics.find((metric) => metric.key === selectedMetric);
                        const selectedMetricCount = draft?.query?.metrics?.length ?? 0;
                        const metricLabel = allowsMultipleMetrics
                            ? selectedMetricCount
                                ? `${selectedMetricCount} Metrics`
                                : 'Select Values'
                            : (selectedMetricField?.label ?? selectedMetric ?? 'Select Value');

                        return (
                            <Col span={8} key={label}>
                                <Form.Item style={{ marginBottom: '0.5rem' }} label={label}>
                                    <MetricDropdown component={component} slot={slot} multiple={allowsMultipleMetrics}>
                                        <DropdownButton
                                            block
                                            size='small'
                                            style={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                            }}
                                            disabled={!isReady}
                                        >
                                            <Row
                                                style={{ width: '100%' }}
                                                wrap={false}
                                                align={'middle'}
                                                gutter={[6, 0]}
                                            >
                                                <Col flex='none'>
                                                    <Avatar
                                                        size={16}
                                                        shape='square'
                                                        icon={getSchemaFieldIcon(
                                                            selectedMetricField,
                                                            token.colorTextTertiary,
                                                            'metric',
                                                        )}
                                                        style={{
                                                            backgroundColor: token.colorBgBase,
                                                            border: `1px solid ${token.colorBorder}`,
                                                        }}
                                                    />
                                                </Col>
                                                <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {metricLabel}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </DropdownButton>
                                    </MetricDropdown>
                                </Form.Item>
                            </Col>
                        );
                    })}
                    {dimensionSlots.map((label, slot) => {
                        const selectedDimension = draft?.query?.dimensions?.[slot]?.field;
                        const selectedDimensionField = schema.dimensions.find(
                            (dimension) => dimension.key === selectedDimension,
                        );
                        const dimensionLabel = selectedDimensionField?.label ?? selectedDimension ?? 'Select Value';

                        return (
                            <Col span={8} key={label}>
                                <Form.Item style={{ marginBottom: '0.5rem' }} label={label}>
                                    <GroupedByDropdown component={component} slot={slot}>
                                        <DropdownButton
                                            block
                                            size='small'
                                            style={{
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                            }}
                                            disabled={!isReady}
                                        >
                                            <Row
                                                style={{ width: '100%' }}
                                                wrap={false}
                                                align={'middle'}
                                                gutter={[6, 0]}
                                            >
                                                <Col flex='none'>
                                                    <Avatar
                                                        size={16}
                                                        shape='square'
                                                        icon={getSchemaFieldIcon(
                                                            selectedDimensionField,
                                                            token.colorTextTertiary,
                                                            'dimension',
                                                        )}
                                                        style={{
                                                            backgroundColor: token.colorBgBase,
                                                            border: `1px solid ${token.colorBorder}`,
                                                        }}
                                                    />
                                                </Col>
                                                <Col flex='auto' style={{ minWidth: 0, textAlign: 'left' }}>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {dimensionLabel}
                                                    </span>
                                                </Col>
                                            </Row>
                                        </DropdownButton>
                                    </GroupedByDropdown>
                                </Form.Item>
                            </Col>
                        );
                    })}
                    {/* <Col span={8}>
                        <Form.Item
                            style={{ marginBottom: '0.5rem' }}
                            label={<small style={{ color: token.colorTextTertiary }}>Sort By</small>}
                        >
                            <SortByDropdown component={component}>
                                <Button
                                    block
                                    size='small'
                                    style={{ padding: '0 0.25rem', justifyContent: 'flex-start', textAlign: 'left' }}
                                    disabled={!isReady}
                                >
                                    <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                        <Col flex='none'>
                                            <Avatar
                                                size={16}
                                                shape='square'
                                                style={{
                                                    backgroundColor: token.colorBgBase,
                                                    border: `1px solid ${token.colorBorder}`,
                                                }}
                                            />
                                        </Col>
                                        <Col flex='none'>
                                            <span style={{ verticalAlign: '-1px' }}>
                                                {selectedFilter ? selectedFilter : 'Select Value'}
                                            </span>
                                        </Col>
                                        <Col flex='auto' />
                                        <Col flex='none'>
                                            <TbDirection
                                                size='1rem'
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    verticalAlign: '-0.2rem',
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </Button>
                            </SortByDropdown>
                        </Form.Item>
                    </Col> */}
                </Row>
            </div>
            {/* )} */}
            {/* <Divider style={{ margin: '1rem 0' }} />
            {caps.xAxis?.metric && (
                <div style={{ padding: '0 1rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>Y Axis</div>
                    <Row gutter={[16, 0]}>
                        <Col span={8}>
                            <Form.Item
                                style={{ marginBottom: '0.5rem' }}
                                label={<small style={{ color: token.colorTextTertiary }}>Metric</small>}
                            >
                                <MetricDropdown component={component}>
                                    <Button
                                        block
                                        size='small'
                                        style={{ padding: '0 0.25rem', justifyContent: 'flex-start', textAlign: 'left' }}
                                        disabled={!isReady}
                                    >
                                        <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                            <Col flex='none'>
                                                <Avatar
                                                    size={16}
                                                    shape='square'
                                                    style={{
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                />
                                            </Col>
                                            <Col flex='none'>
                                                <span style={{ verticalAlign: '-1px' }}>Select Value</span>
                                            </Col>
                                            <Col flex='auto' />
                                            <Col flex='none'>
                                                <TbDirection
                                                    size='1rem'
                                                    style={{
                                                        color: token.colorTextTertiary,
                                                        verticalAlign: '-0.2rem',
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Button>
                                </MetricDropdown>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                style={{ marginBottom: '0.5rem' }}
                                label={<small style={{ color: token.colorTextTertiary }}>Grouped By</small>}
                            >
                                <GroupedByDropdown component={component}>
                                    <Button block size='small' style={{ padding: '0 0.25rem' }} disabled={!isReady}>
                                        <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                            <Col flex='none'>
                                                <Avatar
                                                    size={16}
                                                    shape='square'
                                                    style={{
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                />
                                            </Col>
                                            <Col flex='none'>
                                                <span style={{ verticalAlign: '-1px' }}>Select Value</span>
                                            </Col>
                                            <Col flex='auto' />
                                            <Col flex='none'>
                                                <TbDirection
                                                    size='1rem'
                                                    style={{
                                                        color: token.colorTextTertiary,
                                                        verticalAlign: '-0.2rem',
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Button>
                                </GroupedByDropdown>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                style={{ marginBottom: '0.5rem' }}
                                label={<small style={{ color: token.colorTextTertiary }}>Sort By</small>}
                            >
                                <SortByDropdown component={component}>
                                    <Button block size='small' style={{ padding: '0 0.25rem' }} disabled={!isReady}>
                                        <Row style={{ width: '100%' }} wrap={false} align={'middle'} gutter={[6, 0]}>
                                            <Col flex='none'>
                                                <Avatar
                                                    size={16}
                                                    shape='square'
                                                    style={{
                                                        backgroundColor: token.colorBgBase,
                                                        border: `1px solid ${token.colorBorder}`,
                                                    }}
                                                />
                                            </Col>
                                            <Col flex='none'>
                                                <span style={{ verticalAlign: '-1px' }}>Select Value</span>
                                            </Col>
                                            <Col flex='auto' />
                                            <Col flex='none'>
                                                <TbDirection
                                                    size='1rem'
                                                    style={{
                                                        color: token.colorTextTertiary,
                                                        verticalAlign: '-0.2rem',
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Button>
                                </SortByDropdown>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            )} */}
        </>
    );
};

export default Axes;
